import { useCallback, useEffect, useState } from 'react'
import { lumi } from '@/lib/lumi'

export type HomeBackground = 'white' | 'gray' | 'dark' | 'gradient'

export type HomeSectionType =
  | 'hero'
  | 'universes'
  | 'mission'
  | 'jobs-bnssa'
  | 'jobs-mns'
  | 'formations'
  | 'testimonials'
  | 'faq'
  | 'cta-final'
  | 'custom'

export const NATIVE_SECTION_TYPES: HomeSectionType[] = [
  'hero',
  'universes',
  'mission',
  'jobs-bnssa',
  'jobs-mns',
  'formations',
  'testimonials',
  'faq',
  'cta-final',
]

export const SECTION_TYPE_LABELS: Record<HomeSectionType, string> = {
  hero: 'Hero (bandeau principal)',
  universes: 'Deux métiers, une seule plateforme',
  mission: 'Notre mission',
  'jobs-bnssa': 'Dernières offres BNSSA',
  'jobs-mns': 'Dernières offres MNS',
  formations: 'Formez-vous aux métiers aquatiques',
  testimonials: 'Ils nous font confiance',
  faq: 'FAQ — Questions fréquentes',
  'cta-final': 'CTA final (Prêt à booster…)',
  custom: 'Section personnalisée (HTML libre)',
}

export interface HomeSection {
  _id: string
  sectionKey: string
  sectionType: HomeSectionType
  title: string
  subtitle?: string
  content: string
  ctaLabel?: string
  ctaUrl?: string
  ctaLabel2?: string
  ctaUrl2?: string
  background?: HomeBackground
  order: number
  isActive: boolean
  creator?: string
  createdAt: string
  updatedAt: string
}

export interface NewHomeSection {
  sectionKey: string
  sectionType: HomeSectionType
  title: string
  subtitle?: string
  content: string
  ctaLabel?: string
  ctaUrl?: string
  ctaLabel2?: string
  ctaUrl2?: string
  background?: HomeBackground
  order: number
  isActive: boolean
}

export function useHomeSections() {
  const [items, setItems] = useState<HomeSection[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await lumi.entities.home_sections.list({
        sort: { order: 1 },
      })
      const list = (res.list as HomeSection[]) || []
      // Tolérance pour les anciens documents sans sectionType
      const normalized = list.map((s) => ({
        ...s,
        sectionType: (s.sectionType || 'custom') as HomeSectionType,
      }))
      setItems(normalized)
      console.log('[useHomeSections] fetched:', normalized.length)
    } catch (e) {
      const msg = (e as Error)?.message || 'Erreur de chargement des sections'
      console.error('[useHomeSections] fetch failed:', e)
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const createSection = useCallback(
    async (data: NewHomeSection, creator?: string) => {
      const now = new Date().toISOString()
      const doc = {
        ...data,
        creator: creator ?? 'admin',
        createdAt: now,
        updatedAt: now,
      }
      const created = await lumi.entities.home_sections.create(doc)
      console.log('[useHomeSections] created:', created._id)
      await fetchAll()
      return created
    },
    [fetchAll],
  )

  const updateSection = useCallback(
    async (id: string, patch: Partial<NewHomeSection>) => {
      const now = new Date().toISOString()
      const updated = await lumi.entities.home_sections.update(id, {
        ...patch,
        updatedAt: now,
      })
      console.log('[useHomeSections] updated:', id)
      await fetchAll()
      return updated
    },
    [fetchAll],
  )

  const deleteSection = useCallback(
    async (id: string) => {
      await lumi.entities.home_sections.delete(id)
      console.log('[useHomeSections] deleted:', id)
      await fetchAll()
    },
    [fetchAll],
  )

  const moveSection = useCallback(
    async (id: string, direction: 'up' | 'down') => {
      const sorted = [...items].sort((a, b) => a.order - b.order)
      const idx = sorted.findIndex((i) => i._id === id)
      if (idx === -1) return
      const targetIdx = direction === 'up' ? idx - 1 : idx + 1
      if (targetIdx < 0 || targetIdx >= sorted.length) return
      const item = sorted[idx]
      const target = sorted[targetIdx]
      const now = new Date().toISOString()
      await Promise.all([
        lumi.entities.home_sections.update(item._id, { order: target.order, updatedAt: now }),
        lumi.entities.home_sections.update(target._id, { order: item.order, updatedAt: now }),
      ])
      console.log('[useHomeSections] moved:', id, direction)
      await fetchAll()
    },
    [items, fetchAll],
  )

  return { items, loading, error, fetchAll, createSection, updateSection, deleteSection, moveSection }
}
